// Saved views on every B10 list and report (lane B10, slice S19; plan D6; CR-051 "… saved views, grouping, sorting,
// security-trimmed drill-down and exports."). A saved view is a person's named filter spec, never rows: S10's
// `saved_view.list / save / delete` keep it private to its owner, at most 50 live per surface, validated at save and at
// apply. The server answers each view with `spec` (what still applies for this reader now) and `dropped` (the keys it
// refused now — a dimension the reader's role no longer reaches), so a view never widens anyone's scope: applying it
// hands the list its own filters and the reader's current scope does the rest.
//
// Provided to the other B10 screens (PARALLEL-MAP §2.1 S19): useSavedViews(surface) and
// <SavedViewsSelect surface current onApply hs />. SavedViewsControl is the drawn control with its state passed in
// (the SSR tests render it). The field tables and the query helpers below map a list's filters to its surface's spec
// and back, so a view saved on B2's Requisitions list reapplies through the same address the list already reads, and
// B3's Candidates table takes the select in its slot with the same table (its status and internal/external filters);
// both are registered from ../lists/register.ts (C-B10-13).

import { useEffect, useState } from "react";
import { savedViewDelete, savedViewList, savedViewSave } from "@/api/tools";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { ACTION_LABELS, Banner, Button, FILTERBAR_LABELS, Input, Select, cx } from "@/mc";
import { LIST_LABELS } from "../lists/labels";
import { hsKey } from "../lists/RowSignals";

type ListOutput = Awaited<ReturnType<typeof savedViewList>>;

/** One saved view as `saved_view.list` returns it: {id, name, surface, spec, row_version, created_at, updated_at, dropped}. */
export type SavedView = ListOutput["views"][number];

/** A list's or a report's filter spec: the input of the tool the surface reads, without paging. */
export type ViewSpec = Record<string, unknown>;

/** A view's name is 1–80 characters (B10-M1 `saved_view.name`). */
export const SAVED_VIEW_NAME_MAX = 80;

/** At most this many live views per person and surface (plan D6); the server's own figure wins when it sends one. */
export const SAVED_VIEW_LIMIT = 50;

// ---- specs ----------------------------------------------------------------------------------------------------------

const empty = (value: unknown) =>
  value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items = value.filter((item) => !empty(item)).map(canonical);
    return items.every((item) => typeof item === "string" || typeof item === "number")
      ? [...items].sort((a, b) => String(a).localeCompare(String(b)))
      : items;
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const item = (value as Record<string, unknown>)[key];
      if (!empty(item)) out[key] = canonical(item);
    }
    return out;
  }
  return value;
}

/** A spec in canonical form: keys sorted, empty values (undefined, null, "", []) dropped, lists of words sorted. */
export function canonicalSpec(spec: ViewSpec | null | undefined): string {
  return JSON.stringify(canonical(spec ?? {}));
}

/** Two specs ask for the same rows. */
export function sameSpec(a: ViewSpec | null | undefined, b: ViewSpec | null | undefined): boolean {
  return canonicalSpec(a) === canonicalSpec(b);
}

/** The view whose spec is the one applied now, if any. */
export function matchingView(views: readonly SavedView[], current: ViewSpec): SavedView | undefined {
  const now = canonicalSpec(current);
  return views.find((view) => canonicalSpec(view.spec) === now);
}

const nameKey = (name: string) => name.trim().toLowerCase();

/** The live view with this name, compared as the table's unique index does (`lower(name)`, trimmed). */
export function viewNamed(views: readonly SavedView[], name: string): SavedView | undefined {
  const key = nameKey(name);
  return key === "" ? undefined : views.find((view) => nameKey(view.name) === key);
}

/**
 * What `save(name, …)` writes: a name the person already uses updates that view (its id and the version they read,
 * so a newer save elsewhere is refused 409, never overwritten); a new name creates a view.
 */
export function saveTarget(views: readonly SavedView[], name: string): { id?: string; expectedRowVersion?: number } {
  const view = viewNamed(views, name);
  return view ? { id: view.id, expectedRowVersion: view.row_version } : {};
}

/** The hotspot prefix of a surface's control (`sv.requisitions`, `sv.report.candidate-experience`). */
export function surfaceHs(surface: string): string {
  const segments = surface
    .split(/[:.]/)
    .map((segment) => segment.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, ""))
    .filter(Boolean);
  return ["sv", ...segments].join(".");
}

// ---- a V1 list's URL query ⇄ its surface's spec -----------------------------------------------------------------

/** One filter of a list: the URL parameter it reads and the spec key (the list tool's input field) it is saved as. */
export interface QueryField {
  readonly param: string;
  readonly key: string;
  /** A comma-separated parameter, saved as a list. */
  readonly list?: boolean;
  /**
   * Parameter value → spec value where the two differ (the Requisitions list's owner "me" is the tool's "mine"; the
   * Candidates screen's "internal" / "external" is `candidate.search`'s `internal: true / false`).
   */
  readonly values?: Readonly<Record<string, string | boolean>>;
}

/**
 * The two lists' filters as their surfaces keep them (S10). `requisitions` is V1 `requisition.list`'s input (status,
 * level, owner, department, brand, phase_state, q, sort). `candidates` is B3's `candidate.search` input as the
 * Candidates screen filters it (status, internal/external) and never its `q`: a name, email, phone or LinkedIn handle
 * is a search for one person, never a view, so no candidate's identity is stored in a view or its receipts (Law 9; S10
 * refuses the key). B3's screen holds the two filters itself; its slot (C-B10-13) maps them through this table with
 * `specFromQuery` / `queryFromSpec`.
 */
export const LIST_VIEW_FIELDS = {
  requisitions: [
    { param: "state", key: "status", list: true },
    { param: "level", key: "level" },
    { param: "owner", key: "owner", values: { me: "mine" } },
    { param: "department", key: "department" },
    { param: "brand", key: "brand" },
    { param: "phase", key: "phase_state" },
    { param: "q", key: "q" },
    { param: "sort", key: "sort" },
  ],
  candidates: [
    { param: "status", key: "status" },
    { param: "internal", key: "internal", values: { internal: true, external: false } },
  ],
} as const satisfies Readonly<Record<string, readonly QueryField[]>>;

/** The spec a list's URL query asks for (empty parameters left out). */
export function specFromQuery(params: URLSearchParams, fields: readonly QueryField[]): ViewSpec {
  const spec: ViewSpec = {};
  for (const field of fields) {
    const raw = params.get(field.param);
    if (raw === null || raw === "") continue;
    if (field.list) {
      const items = raw.split(",").filter(Boolean).map((item) => field.values?.[item] ?? item);
      if (items.length) spec[field.key] = items;
    } else {
      spec[field.key] = field.values?.[raw] ?? raw;
    }
  }
  return spec;
}

const paramValue = (field: QueryField, value: unknown): string => {
  const back = Object.entries(field.values ?? {}).find(([, specValue]) => specValue === value);
  return back ? back[0] : String(value);
};

/** The URL query that asks for `spec`, keeping the named parameters of `keep` (the list's table/board choice). */
export function queryFromSpec(
  spec: ViewSpec,
  fields: readonly QueryField[],
  keep: URLSearchParams = new URLSearchParams(),
  keepParams: readonly string[] = [],
): URLSearchParams {
  const out = new URLSearchParams();
  for (const param of keepParams) {
    const value = keep.get(param);
    if (value !== null && value !== "") out.set(param, value);
  }
  for (const field of fields) {
    const value = spec[field.key];
    if (empty(value)) continue;
    if (field.list) {
      const items = (Array.isArray(value) ? value : [value]).filter((item) => !empty(item)).map((item) => paramValue(field, item));
      if (items.length) out.set(field.param, items.join(","));
    } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out.set(field.param, paramValue(field, value));
    }
  }
  return out;
}

// ---- the hook -------------------------------------------------------------------------------------------------------

export interface SavedViewsState {
  views: SavedView[];
  /** The live-view bound the server reports for the surface. */
  limit: number;
  loading: boolean;
  /** The last refusal of the list read or of a write (AppError), else null. */
  error: Error | null;
  /** The list read was refused (the seat may not keep views of this surface). */
  refused: boolean;
  busy: boolean;
  /** Creates a view, or with `id` and the version read, renames or re-specs that view (stale → 409 STALE). */
  save(name: string, spec: ViewSpec, id?: string, expectedRowVersion?: number): Promise<SavedView>;
  /** Archives the view (it leaves the list; the row and its receipts stay). */
  remove(id: string, expectedRowVersion: number): Promise<void>;
}

/** PARALLEL-MAP §2.1 S19: the reader's live views of one surface and the two writes, each refreshing the list. */
export function useSavedViews(surface: string, opts: { enabled?: boolean } = {}): SavedViewsState {
  const key = ["saved-views", surface] as const;
  const list = useRead([...key], () => savedViewList({ surface }), { enabled: opts.enabled ?? true });
  const saving = useWrite((input: Parameters<typeof savedViewSave>[0], idempotencyKey) => savedViewSave(input, idempotencyKey), [key]);
  const removing = useWrite((input: Parameters<typeof savedViewDelete>[0], idempotencyKey) => savedViewDelete(input, idempotencyKey), [key]);
  return {
    views: list.data?.views ?? [],
    limit: list.data?.limit ?? SAVED_VIEW_LIMIT,
    loading: list.isLoading,
    error: list.error ?? saving.error ?? removing.error ?? null,
    refused: isClientRefusal(list.error),
    busy: saving.isPending || removing.isPending,
    save: async (name, spec, id, expectedRowVersion) => {
      const answer = await saving.mutateAsync({
        surface,
        name: name.trim(),
        spec,
        ...(id !== undefined ? { id } : {}),
        ...(expectedRowVersion !== undefined ? { expected_row_version: expectedRowVersion } : {}),
      });
      return answer.view;
    },
    remove: async (id, expectedRowVersion) => {
      await removing.mutateAsync({ id, expected_row_version: expectedRowVersion });
    },
  };
}

// ---- the control ----------------------------------------------------------------------------------------------------

export interface SavedViewsControlProps {
  views: readonly SavedView[];
  /** The view applied now ("" for none). */
  selectedId: string;
  onSelect: (id: string) => void;
  /** The name field is open (after *Save view*). */
  editing: boolean;
  name: string;
  onName: (name: string) => void;
  onStartSave: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Absent: no *Archive* (a seat that may not delete). */
  onArchive?: ((view: SavedView) => void) | undefined;
  loading?: boolean | undefined;
  busy?: boolean | undefined;
  /** The server's sentence after a refused write (DC-24 refused); the name entered is kept. */
  refused?: string | null | undefined;
  /** The keys the applied view held that no longer apply for this reader. */
  dropped?: readonly string[] | undefined;
  /** A dimension key's words (his §7.2 filter names), where the host has them; else only the notice. */
  dimensionLabel?: ((key: string) => string | undefined) | undefined;
  hs: string;
  className?: string | undefined;
}

/** The drawn control: *Saved views* select, *Archive* for the one applied, *Save view* opening the name field. */
export function SavedViewsControl({
  views, selectedId, onSelect, editing, name, onName, onStartSave, onConfirm, onCancel, onArchive, loading, busy, refused,
  dropped = [], dimensionLabel, hs, className,
}: SavedViewsControlProps) {
  const selected = selectedId ? views.find((view) => view.id === selectedId) : undefined;
  const names = dropped.map((key) => dimensionLabel?.(key)).filter((word): word is string => Boolean(word));
  return (
    <div className={cx("mc-fbar-views", className)}>
      <label className="mc-fbar-l">
        <span className="mc-fbar-lt">{FILTERBAR_LABELS.savedViews}</span>
        <Select
          sm
          options={views.map((view) => ({ value: view.id, label: view.name }))}
          value={selected ? selected.id : ""}
          onChange={(id) => onSelect(id)}
          loading={loading}
          hs={hsKey(hs, "select")}
          className="mc-fbar-sel"
        />
      </label>
      {selected && onArchive && !editing ? (
        <Button sm tone="ghost" icon="archive" busy={busy} onClick={() => onArchive(selected)} hs={hsKey(hs, "archive")}>
          {LIST_LABELS.archive}
        </Button>
      ) : null}
      {editing ? (
        <form
          className="mc-fbar-l"
          onSubmit={(event) => {
            event.preventDefault();
            onConfirm();
          }}
        >
          <Input
            sm
            aria-label={LIST_LABELS.savedView}
            placeholder={LIST_LABELS.savedView}
            maxLength={SAVED_VIEW_NAME_MAX}
            required
            value={name}
            onChange={(event) => onName(event.currentTarget.value)}
            hs={hsKey(hs, "name")}
          />
          <Button sm type="submit" busy={busy} hs={hsKey(hs, "confirm")}>{ACTION_LABELS.save}</Button>
          <Button sm tone="ghost" onClick={onCancel} hs={hsKey(hs, "cancel")}>{ACTION_LABELS.cancel}</Button>
        </form>
      ) : (
        <Button sm tone="ghost" icon="plus" onClick={onStartSave} hs={hsKey(hs, "save")}>{ACTION_LABELS.saveView}</Button>
      )}
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {dropped.length > 0 ? (
        <Banner tone="warn">
          {LIST_LABELS.subjectToPermission}
          {names.length ? ` · ${names.join(" · ")}` : null}
        </Banner>
      ) : null}
    </div>
  );
}

export interface SavedViewsSelectProps {
  /** `requisitions`, `candidates`, `action_center`, `workbench` or `report:<family>` (S10's surfaces). */
  surface: string;
  /** The spec the host applies now (its filters, sort and grouping). */
  current: ViewSpec;
  /** Applies a chosen view: the spec that still applies for this reader and the keys the server dropped. */
  onApply: (spec: ViewSpec, dropped: string[]) => void;
  /** Hotspot prefix (default `sv.<surface>`). */
  hs?: string | undefined;
  dimensionLabel?: ((key: string) => string | undefined) | undefined;
  className?: string | undefined;
}

/**
 * PARALLEL-MAP §2.1 S19: the saved views of one surface for the signed-in person. Absent when the manifest has no
 * `saved_view.list` or the read is refused (deny by default). A view stays shown as applied until the host's spec
 * moves away from what it applied; saving under a name already used updates that view.
 */
export function SavedViewsSelect({ surface, current, onApply, hs, dimensionLabel, className }: SavedViewsSelectProps) {
  const { can } = useSession();
  const allowed = can("saved_view.list");
  const state = useSavedViews(surface, { enabled: allowed });
  // The view applied last: `base` is the host's spec before it, `result` the spec the host settled on after it (a
  // view whose dropped keys no longer apply settles on less than its own spec, so matching specs alone would lose it).
  const [applied, setApplied] = useState<{ id: string; base: string; result?: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [dropped, setDropped] = useState<string[]>([]);
  const [refused, setRefused] = useState<string | null>(null);
  const now = canonicalSpec(current);
  useEffect(() => {
    if (applied && applied.result === undefined && now !== applied.base) setApplied({ ...applied, result: now });
  }, [applied, now]);
  if (!allowed || state.refused) return null;
  const latched = applied && (applied.result === undefined || applied.result === now)
    ? state.views.find((view) => view.id === applied.id)
    : undefined;
  const selected = latched ?? matchingView(state.views, current);
  const prefix = hs ?? surfaceHs(surface);

  const select = (id: string) => {
    setRefused(null);
    const view = id ? state.views.find((candidate) => candidate.id === id) : undefined;
    if (!view) {
      setApplied(null);
      setDropped([]);
      return;
    }
    const lost = [...(view.dropped ?? [])];
    setDropped(lost);
    setApplied(sameSpec(view.spec, current) ? null : { id: view.id, base: now });
    onApply({ ...view.spec }, lost);
  };
  const confirm = () => {
    const words = name.trim();
    if (!words || state.busy) return;
    const target = saveTarget(state.views, words);
    state
      .save(words, { ...current }, target.id, target.expectedRowVersion)
      .then(() => {
        setEditing(false);
        setApplied(null);
        setDropped([]);
        setRefused(null);
      })
      .catch((error: unknown) => setRefused(errorText(error)));
  };
  const archive = (view: SavedView) => {
    setRefused(null);
    state
      .remove(view.id, view.row_version)
      .then(() => {
        if (applied?.id === view.id) setApplied(null);
      })
      .catch((error: unknown) => setRefused(errorText(error)));
  };

  return (
    <SavedViewsControl
      views={state.views}
      selectedId={selected?.id ?? ""}
      onSelect={select}
      editing={editing}
      name={name}
      onName={setName}
      onStartSave={() => {
        setRefused(null);
        setName(selected?.name ?? "");
        setEditing(true);
      }}
      onConfirm={confirm}
      onCancel={() => {
        setEditing(false);
        setRefused(null);
      }}
      onArchive={can("saved_view.delete") ? archive : undefined}
      loading={state.loading}
      busy={state.busy}
      refused={refused}
      dropped={dropped}
      dimensionLabel={dimensionLabel}
      hs={prefix}
      className={className}
    />
  );
}
